import React from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import classNames from "classnames";
// Actions
import {
  errorResponse,
  setCustomProtocolInfo,
  setView,
  setVirtualKeyboard,
  enterAddNewLocation,
  setCurrentMapId,
} from "../../actions";

// MUI Components
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import CircularProgress from "@material-ui/core/CircularProgress";
import TextField from "@material-ui/core/TextField";
import FormControl from "@material-ui/core/FormControl";
import FormGroup from "@material-ui/core/FormGroup";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Checkbox from "@material-ui/core/Checkbox";
import Grid from "@material-ui/core/Grid";
import Card from "@material-ui/core/Card";

// Other Files
import { customTheme } from "../../constants/customTheme";
import { colors } from "../../constants/colors";
import { viewIds } from "../../constants/componentMap";
import SnackbarInfo from "../Shared/Snackbar";
import Header from "../Header/Header";
import VirtualKeyboard from "../Shared/VirtualKeyboard";

const useStyles = makeStyles({
  fill: {
    flex: "1 0 auto",
  },
  cardRoot: (props) => ({
    ...customTheme(props.theme).card.root,
    "@media only screen and (max-width: 900px)": {
      width: "80%",
    },
    width: "65%",
  }),
  title: {
    ...customTheme().card.title,
    paddingBottom: "0px",
  },
  buttonContainer: {
    ...customTheme().card.buttonContainer,
    paddingTop: "15px",
  },
  contentItem: customTheme().card.contentItem,
  icon: customTheme().icon,
  buttonProgress: (props) => customTheme(props.theme).buttonProgress,
  mapControl: {
    flexDirection: "row",
  },
  copyright: customTheme().copyright,
  button: {
    "&:hover": {
      backgroundColor: (props) => colors[props.theme].primaryLight,
    },
  },
});

const CreateCustomProtocol = (props) => {
  const classes = useStyles(props);

  const initialState = () => {
    let initialState = { protocolName: "", protocolId: "", maps: {} };
    for (let map in props.maps) {
      initialState = {
        ...initialState,
        maps: { ...initialState.maps, [map]: true },
      };
    }
    return initialState;
  };

  const [newProtocol, setProtocol] = React.useState(initialState());
  const [submitted, setSubmitted] = React.useState(false);
  const [keyboardInput, setKeyboardInput] = React.useState({
    inputName: "protocolName",
    inputUpdate: { protocolName: "" },
  });

  const handleFormChange = (input_value) => (event) => {
    if (input_value.field === "protocolName") {
      let newProtocolName = event.target ? event.target.value : event;
      const id =
        "CUSTOM_" +
        newProtocolName
          .replace(/[^a-zA-Z0-9 _]/g, "")
          .replace(/ /g, "_")
          .toUpperCase(); //remove special characters (anything thats not a letter), replace spaces with underscore, turn all letters upper case

      setProtocol({
        ...newProtocol,
        [input_value.field]: newProtocolName,
        protocolId: id,
      });
      if (event.target) {
        // Update virtualKeyboard with the manually entered values
        setKeyboardInput({
          ...keyboardInput,
          inputUpdate: { [input_value.field]: event.target.value },
        });
      }
    } else if (input_value.field === "map") {
      setProtocol({
        ...newProtocol,
        maps: {
          ...newProtocol.maps,
          [input_value.type]: !newProtocol.maps[input_value.type],
        },
      });
    }
    handleInputCheck();
  };

  const handleInputCheck = () => {
    let error = { state: false, message: "" };
    let noMaps = true;
    for (let map of Object.keys(newProtocol.maps)) {
      if (newProtocol.maps[map]) {
        noMaps = false;
      }
    }
    if (props.allProtocolIds.includes(newProtocol.protocolId)) {
      error = {
        state: true,
        message: "Protocol ID is taken. Enter a different name",
      };
    } else if (props.allProtocolNames.includes(newProtocol.protocolName)) {
      error = {
        state: true,
        message: "Protocol name is taken. Enter a different name",
      };
    } else if (noMaps) {
      error = {
        state: true,
        message: "You must select at least one body map.",
      };
    }
    return error;
  };

  const handleSubmit = () => {
    let result = handleInputCheck();
    if (result.state === true) {
      props.errorResponse(result.message);
    } else {
      let maps = [];
      for (let map of Object.keys(newProtocol.maps)) {
        // create array of maps in custom protocol
        if (newProtocol.maps[map]) {
          maps.push(map);
        }
      }
      props.enterAddNewLocation(maps);
      props.setCurrentMapId(maps[0]);
      props.setCustomProtocolInfo(newProtocol);
      setSubmitted(true); //change to circular progress icon
      // Jump to the "add New Location" view
      props.setView(viewIds.mainApp);
      // Close an open virtual keyboard on exiting the page
      props.setVirtualKeyboard(false);
    }
  };

  const handleButtonDisable = () => {
    let disable = false;

    if (
      newProtocol.protocolName.length === 0 ||
      newProtocol.protocolId.length === 0
    ) {
      disable = true;
    }
    return disable;
  };

  return (
    <Grid
      item
      container
      className={classNames(classes.fill, "noselect")}
      direction="column"
    >
      {/* HEADER */}
      <Header
      // eventSource={props.eventSource}
      />
      <Grid
        item
        container
        className={classes.fill}
        direction="column"
        justify="space-between"
      >
        <Grid
          item
          container
          className={classes.fill}
          direction="column"
          justify="center"
          alignItems="center"
        >
          {/* <Grid item> */}
          {/* INFORMATION CARD */}
          <Card className={classes.cardRoot}>
            <Typography
              variant="h5"
              color="textPrimary"
              align="center"
              className={classes.title}
            >
              Create Protocol
            </Typography>

            <TextField
              className={classes.contentItem}
              fullWidth
              required
              label="Protocol Name"
              value={newProtocol.protocolName}
              onFocus={() => {
                props.setVirtualKeyboard(true);
              }}
              onChange={handleFormChange({
                type: null,
                field: "protocolName",
              })}
            />
            <FormControl
              onFocus={() => {
                props.setVirtualKeyboard(false);
              }}
              className={classes.contentItem}
            >
              <Typography> Maps: </Typography>
              <FormGroup className={classes.mapControl}>
                {Object.keys(props.maps).map((map) => {
                  return (
                    <FormControlLabel
                      key={map}
                      control={
                        <Checkbox
                          color="primary"
                          onChange={handleFormChange({
                            type: map,
                            field: "map",
                          })}
                          checked={newProtocol.maps[map]}
                          value={newProtocol.maps[map]}
                        />
                      }
                      label={props.maps[map].name}
                    />
                  );
                })}
              </FormGroup>
            </FormControl>
            <div className={classes.buttonContainer}>
              <Button
                onClick={() => handleSubmit()}
                variant="contained"
                color="primary"
                disabled={handleButtonDisable()}
                className={classes.button}
              >
                {submitted && (
                  <CircularProgress
                    size={25}
                    className={classes.buttonProgress}
                  />
                )}
                Continue
              </Button>
            </div>
          </Card>
          {/* </Grid> */}
        </Grid>
      </Grid>
      {/* COPYRIGHT INFO */}
      <Grid item align="center" className={classes.copyright}>
        <Typography variant="caption" gutterBottom>
          This software is for investigational research use only. It is provided
          as-is with no warranty. &copy;{new Date().getFullYear()} MicroElastic
          Ultrasound Systems
        </Typography>
      </Grid>
      {/* SNACKBAR FOR MESSAGE DISPLAY */}
      <SnackbarInfo />
      <VirtualKeyboard
        anchor={"center"}
        keyboardInitialInput={{ protocolName: newProtocol.protocolName }}
        keyboardInputUpdate={keyboardInput.inputUpdate}
        keyboardInputName={keyboardInput.inputName}
        keyboardChange={handleFormChange({
          type: null,
          field: keyboardInput.inputName,
        })}
        keyboardEnter={() => {
          if (!handleButtonDisable()) {
            handleSubmit();
          }
        }}
      />
    </Grid>
  );
};

const mapDispatchToProps = (dispatch) => {
  return bindActionCreators(
    {
      errorResponse,
      setCustomProtocolInfo,
      setView,
      setVirtualKeyboard,
      enterAddNewLocation,
      setCurrentMapId,
    },
    dispatch
  );
};

const mapStateToProps = (state) => {
  const allProtocolNames = [];
  const allProtocolIds = [];
  for (let protocolId of Object.keys(state.availableProtocols.protocolsById)) {
    allProtocolIds.push(protocolId);
    allProtocolNames.push(
      state.availableProtocols.protocolsById[protocolId].name
    );
  }

  return {
    allProtocolIds: allProtocolIds,
    allProtocolNames: allProtocolNames,
    maps: state.protocol.maps,
    theme: state.utility.theme,
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(CreateCustomProtocol);
